import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Eye, ShoppingBag, TrendingUp, Star, Image as ImageIcon } from "lucide-react";
import { euro } from "@/lib/format";
import { toast } from "sonner";

const Stats = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/stats")
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const t = data?.totals || {};
  const kpis = [
    { label: "Photos vues", value: t.views ?? 0, icon: Eye },
    { label: "Ventes", value: t.sales ?? 0, icon: ShoppingBag },
    { label: "Revenus photos", value: euro(t.revenue), icon: TrendingUp },
    { label: "Abonnés", value: t.active_subscribers ?? 0, icon: Star },
  ];

  return (
    <div className="space-y-5">
      <h1 className="font-display text-2xl">Statistiques créateur</h1>

      <div className="grid grid-cols-2 gap-3">
        {kpis.map((k) => {
          const Icon = k.icon;
          return (
            <Card key={k.label} className="p-4 rounded-2xl">
              <Icon className="h-5 w-5 text-primary" />
              <p className="text-xs text-muted-foreground mt-3">{k.label}</p>
              <p className="font-display text-xl mt-0.5" data-testid={`stat-${k.label}`}>{loading ? "…" : k.value}</p>
            </Card>
          );
        })}
      </div>

      <div>
        <h2 className="font-display text-lg mb-2">Par photo</h2>
        {loading ? (
          <Skeleton className="h-40 rounded-2xl" />
        ) : !data || data.photos.length === 0 ? (
          <Card className="p-8 rounded-2xl text-center text-muted-foreground">
            <ImageIcon className="h-8 w-8 mx-auto mb-2" />
            <p>Vous n'avez encore envoyé aucune photo.</p>
          </Card>
        ) : (
          <Card className="rounded-2xl overflow-hidden" data-testid="stats-photos-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Photo</TableHead>
                  <TableHead className="text-center">Vues</TableHead>
                  <TableHead className="text-center">Vendue</TableHead>
                  <TableHead className="text-right">Revenu</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.photos.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>
                      <div className="font-medium text-sm truncate max-w-[120px]">{p.caption || "Sans légende"}</div>
                      <div className="text-[11px] text-muted-foreground">{euro(p.price)} · @{p.receiver?.username}</div>
                    </TableCell>
                    <TableCell className="text-center tabular-nums">{p.views}</TableCell>
                    <TableCell className="text-center">
                      {p.sold ? <Badge className="bg-[hsla(152,72%,45%,0.18)] text-[hsl(152_72%_60%)] border-0">Oui</Badge> : <Badge variant="secondary">Non</Badge>}
                    </TableCell>
                    <TableCell className="text-right font-medium">{p.sold ? euro(p.revenue) : "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Stats;
